/**
 * Help Center: "contact" section articles. Format and rules: src/content/help/README.md.
 */
export default [
  {
    slug: 'how-to-contact',
    title: 'How to reach Defera',
    summary: 'Chat with the Defera Concierge or call {{SUPPORT_PHONE}}: our AI concierge answers both, 24/7. Or send us a message from the Contact page for a reply within 24 hours.',
    keywords: ['contact us', 'customer service', 'support', 'phone number', 'email address', 'contact form', 'send a message', 'mailing address', 'help'],
    related: ['contact/defera-concierge', 'contact/talk-to-a-person', 'contact/how-requests-work', 'contact/urgent-help-before-a-flight'],
    updated: '2026-10-23',
    body: `You can reach us by chat, by phone or with a message from our [Contact page](/contact#message). Chat and phone are both answered by the Defera Concierge, our AI concierge, 24/7. Our team replies within 24 hours.

The concierge can answer questions about how Defera works and about your own trip, at any time of day. When something needs a person, it sends a request to our team for you.

## Your options at a glance

Here's how the ways to reach us compare:

| Channel | Where | When |
|---|---|---|
| Chat | [Chat with the Defera Concierge](/support) | 24/7 |
| Phone | [{{SUPPORT_PHONE}}](tel:{{SUPPORT_PHONE_TEL}}) | 24/7 |
| Message | [Send us a message](/contact#message) | Replies within 24 hours |

Chat and phone reach the same AI concierge, so pick whichever you prefer. The message form is there if you'd rather write.

## Chatting with the Defera Concierge

The Defera Concierge is our AI concierge. It can explain how Defera works, answer questions about your trip, point you to the right button in the app and send a request to our team when something needs a person.

You can start a chat from the support page, or from any trip: open **Trips**, tap the trip, and use the message button in the trip's header row. [Chatting with the Defera Concierge](/help/contact/defera-concierge) explains what it can do.

## Calling us

Call [{{SUPPORT_PHONE}}](tel:{{SUPPORT_PHONE_TEL}}) any time. The same AI concierge answers by voice, with the same knowledge as the chat, so call whenever you'd rather talk than type. Each trip's header row also has a button to call us.

Calling isn't a faster way to reach us, and it isn't a way to reach a person. If something needs a person, the concierge sends a request to our team, the same as in chat.

Before it shares any account details on a call, the concierge asks for your booking reference (or the email on your account) and the passenger's name. Your booking reference is the code at the top of your booking confirmation email.

## Sending us a message

Send us a message from the [Contact page](/contact#message). Our team replies within 24 hours. Include your booking reference so we can find your trip quickly.

If you're signed in, all it needs is your name and your message. It also appears in your Defera chat, and our reply comes there and by email. If you're not signed in, add your email address so we can reply to you there.

## Writing to us by mail

Our mailing address is Defera LLC, 28 Geary St, Suite 650 #321, San Francisco, CA 94108. Mail takes longer than the other options, so for anything about an upcoming trip, chat or a message is quicker.

## When you need a person

The concierge can send a request to our team for you, and it asks before sending anything. Your request shows as a card on your trip page. The answer posts there and comes to you by email, usually within 24 hours. [How requests to our team work](/help/contact/how-requests-work) explains each step.

## If it's urgent

If something's wrong with a flight that's coming up soon, tell the concierge it's urgent. An urgent request is the fastest way to reach a person on our team. [Getting urgent help before your flight](/help/contact/urgent-help-before-a-flight) shows what to include.`,
  },
  {
    slug: 'defera-concierge',
    title: 'Chatting with the Defera Concierge',
    summary: 'The Defera Concierge is our AI concierge, in chat and by phone, 24/7. It answers your questions, points you to the right button and sends requests to our team.',
    keywords: ['AI assistant', 'chatbot', 'support chat', 'virtual assistant', 'is it a bot', 'help chat', 'ask a question'],
    related: ['contact/how-requests-work', 'contact/talk-to-a-person', 'contact/how-to-contact', 'contact/check-request-status'],
    updated: '2026-10-05',
    body: `The Defera Concierge is our AI concierge. You can chat with it in [the support chat](/support), or call it at [{{SUPPORT_PHONE}}](tel:{{SUPPORT_PHONE_TEL}}), 24/7. It can explain how Defera works, answer questions about your trip, point you to the right button in the app, and send a request to our team when something needs a person.

It's an AI, not a person, and it's there day and night. Here's what it can help with, and what happens when your question needs someone on our team.

## Ways to start a chat

- **Go to the support chat.** It's available 24/7.
- **Start from your trip.** Open **Trips**, tap your trip, and use the message button in the trip's header row.
- **Tap a request button.** Some buttons on your trip, like **Request Flight Change** and **Change Seat Selection**, open the chat with your request already written. The concierge picks it up from there.

## What it can help with

- **How Defera works.** Payment plans, Defera Credit, Defera Miles, your confirmation number and more.
- **Your own trip.** It can usually see your trips, so it can answer with your details rather than in general terms.
- **The right next step.** Many things are self-serve, like **Change Schedule**, **Skip Payment**, **Update Card** and **Make a Payment**. The concierge tells you which one fits.
- **Finding flights.** In chat, it can often search for flights with you. By phone it can't run searches, so search on [Book](/search), and it can help you make sense of what comes back.
- **Requests to our team.** For anything that needs a person, it writes up a request and sends it to our team.

## When your question needs a person

Some things need someone on our team, like an exception to a policy or a change our team makes for you. For those, the concierge sends a request instead of deciding itself. Here's what happens:

1. **It tells you what it will send.** The concierge explains your request and asks before sending it.
2. **You say yes.** Nothing goes to our team until you agree.
3. **Your request shows on your trip page.** It appears as a card marked "Submitted · in progress" while our team works on it.
4. **The answer comes to you.** When our team answers, it posts on that card and comes to you by email, usually within 24 hours.

If it's urgent, for example a problem with a flight that's coming up soon, say so. An urgent request is the fastest way to reach a person. [How requests to our team work](/help/contact/how-requests-work) has the details.

## It's AI, so check your trip page

The Defera Concierge is an AI, and like any AI, it can make mistakes. Your trip page and payment schedule are the source of truth. If something the concierge says doesn't match what your trip shows, go by your trip page and ask the concierge to take another look.

## Calling instead

Calling reaches the same concierge, by voice, with the same knowledge. It's there for when you'd rather talk than type. It isn't a faster lane, and it isn't a way to reach a person: requests to our team work the same way by phone as in chat.

Before it shares account details on a call, it asks for your booking reference (or the email on your account) and the passenger's name. Your booking reference is the code at the top of your booking confirmation email.`,
  },
  {
    slug: 'talk-to-a-person',
    title: 'Can I talk to a person?',
    summary: 'Chat and phone are answered by our AI concierge. When you need a person, it sends a request to our team, and the answer usually comes within 24 hours.',
    keywords: ['human', 'real person', 'representative', 'speak to someone', 'customer service', 'agent', 'supervisor'],
    related: ['contact/how-requests-work', 'contact/urgent-help-before-a-flight', 'contact/defera-concierge', 'contact/how-to-contact'],
    updated: '2026-10-23',
    body: `Our chat and phone line are both answered by the Defera Concierge, our AI concierge, so you won't reach a person there. When something needs a person, the concierge sends a request to our team. Someone on our team looks at it, and the answer usually comes within 24 hours.

Many questions can be answered right in the chat, at any time of day. For the ones that can't, a request is how a person gets involved. Here's how it works.

## How a person gets involved

1. **Tell the concierge what you need.** [Chat with the Defera Concierge](/support), or call, and explain your question. It may be able to answer it right there.
2. **It offers to send a request.** If your question needs a person, the concierge tells you what it will send to our team and asks whether you'd like it sent.
3. **You say yes.** Your request goes to our team and shows as a card on your trip page, marked "Submitted · in progress".
4. **A person on our team answers.** The answer posts on that card and comes to you by email.

You don't need to stay in the chat or keep checking your trip page. The answer comes to you.

## What needs a person?

The concierge can answer many questions itself, like how your payment plan works or where to find a button. It sends a request to our team for things that need a person, such as:

- asking for an exception to a policy, for example because of an illness;
- a change our team makes for you, like new seats;
- a charge or trip total that looks wrong;
- a confirmation number that's taking longer than expected.

If you're not sure whether yours needs a person, ask. The concierge will tell you, and offer to send a request if it does.

## How long it takes

Our team usually answers within 24 hours. The answer comes in writing: it posts on your request's card and comes to you by email. If you have a follow-up, chat with the concierge again and mention your request, and it can send a follow-up to our team.

If your question is about something time-critical, like a flight that's coming up soon, tell the concierge it's urgent. [How requests to our team work](/help/contact/how-requests-work) explains each step in more detail.

## If it's urgent

If you have an urgent travel problem, like trouble with a flight that's coming up soon or a problem during your trip, tell the concierge it's urgent. An urgent request is the fastest way to reach a person on our team. [Getting urgent help before your flight](/help/contact/urgent-help-before-a-flight) covers what to include.

## Does calling reach a person?

No. Calling [{{SUPPORT_PHONE}}](tel:{{SUPPORT_PHONE_TEL}}) reaches the same AI concierge, by voice, with the same knowledge as the chat. It's there for when you'd rather talk than type, but it isn't a faster way to reach our team. Either way, a request is how your question gets to a person.

## Prefer to write?

You can also send us a message from the [Contact page](/contact#message). Our team replies within 24 hours. Include your booking reference, the code at the top of your booking confirmation email, so we can find your trip quickly.`,
  },
  {
    slug: 'how-requests-work',
    title: 'How requests to our team work',
    summary: 'When something needs a person, the Defera Concierge sends a request to our team. Follow it on your trip page; the answer usually comes within 24 hours.',
    keywords: ['inquiry', 'support request', 'response time', 'how long', 'follow up', 'submitted in progress', 'trip page card'],
    related: ['contact/check-request-status', 'contact/talk-to-a-person', 'contact/urgent-help-before-a-flight', 'confirmation-and-travel/when-confirmation-number'],
    updated: '2026-10-05',
    body: `When something needs a person, the Defera Concierge, our AI concierge, writes up a request and sends it to our team for you. Our team usually answers within 24 hours.

You can follow your request on your trip page, where it shows as a card. When our team answers, the answer appears on that card, and the same answer comes to you by email, so there's no need to keep checking.

## What a request is

A request is a short written note to our team about something the concierge can't do for you itself, like changing your seats or looking into a charge. The concierge writes it for you from what you've told it, so there's no form to fill in.

## How a request gets sent

Here's what happens:

1. **You tell the concierge what you need.** Start a chat in [the support chat](/support), or call. Some buttons on your trip, like **Request Flight Change** and **Change Seat Selection**, open the chat with your request already written.
2. **The concierge checks with you.** It explains what it will send and asks if you'd like it sent. It may also ask for details our team will need, like your new dates or seat preferences.
3. **It goes to our team.** Once you say yes, your request is sent, and a card appears on your trip page.

## Requests that are sent for you

A few requests don't need a chat at all. They're sent automatically:

- **Confirmation number release.** When your final payment goes through, a "Confirmation Number Release" request is sent to our team for your trip.
- **Gift card redemptions.** When you redeem Defera Miles for an e-gift card on the **Rewards** page, your request goes to our team as soon as you submit it.

## Where you'll see it

Open **Trips** and tap your trip. Your request shows as a card that reads "Submitted · in progress" while our team works on it. Once it's answered, the card shows the answer, and the same answer comes to you by email. The card stays on your trip page until you dismiss it.

Gift card requests show as a status card on your **Trips** page and on **Rewards**.

## How long it takes

Most requests share the same timing, but two have their own:

| Request | When to expect it |
|---|---|
| Most requests | Usually within 24 hours |
| Confirmation number, final payment Monday through Thursday, or Friday morning (Pacific time) | Within 24 hours |
| Confirmation number, final payment on a Friday afternoon or over the weekend | Monday, or Tuesday at the latest |
| E-gift card | Delivered within 3–5 business days, often sooner |

Our team releases confirmation numbers Monday through Friday, Pacific time. So if your final payment goes through on a Saturday, look for your confirmation number on Monday, or Tuesday at the latest.

## If it's urgent

Tell the concierge it's urgent, and why. An urgent request is the fastest way to reach a person on our team. [Getting urgent help before your flight](/help/contact/urgent-help-before-a-flight) shows what to include.

## Checking on a request

Your trip page is the place to look. If a request seems to be taking longer than expected, [Checking on a request](/help/contact/check-request-status) covers what to do.`,
  },
  {
    slug: 'check-request-status',
    title: 'Checking on a request',
    summary: 'Open the trip in Trips: your request shows as a card, “Submitted · in progress” until our team answers. The answer also comes to you by email.',
    keywords: ['request status', 'track my request', 'inquiry status', 'no reply', 'waiting for an answer', 'follow up', 'taking too long'],
    related: ['contact/how-requests-work', 'confirmation-and-travel/confirmation-taking-longer', 'rewards/where-is-my-gift-card', 'emails/missing-email'],
    updated: '2026-10-23',
    body: `To check on a request, open **Trips** and tap the trip it's about. Your request shows as a card on that trip's page. It reads "Submitted · in progress" while our team works on it, then shows the answer once it's resolved.

You don't need to keep checking, though. When our team answers, the same answer comes to you by email.

## Where to look

Where your request shows depends on what it's about:

- **A request about a trip:** a card on that trip's page.
- **Your confirmation number:** a "Confirmation Number Release" card on the trip, added automatically once your final payment goes through.
- **A gift card redemption:** a status card on your **Trips** page and on **Rewards**.
- **Your email:** when a request is resolved, the answer is emailed to you.

The card stays on your trip page until you dismiss it, so you can come back to the answer later.

## How long requests take

Our team usually answers within 24 hours. Two kinds of request have their own timing:

- **Confirmation number release:** within 24 hours if your final payment goes through Monday through Thursday, or Friday morning (Pacific time). If it goes through on a Friday afternoon or over the weekend, expect it on Monday, or Tuesday at the latest.
- **E-gift cards:** delivered within 3–5 business days, often sooner.

[How requests to our team work](/help/contact/how-requests-work) explains the whole process.

## What if it's taking longer?

- **Waiting on a confirmation number?** If it's past the expected time, or you don't see a "Confirmation Number Release" card on your trip, tap **Finished paying for your trip?** in your trip's payment schedule. It sends the request if one wasn't sent, or confirms that yours is already being handled.
- **Don't see a card at all?** The concierge asks before it sends a request, so if you're not sure yours went through, ask it.
- **Anything else?** [Chat with the Defera Concierge](/support), our AI concierge, and mention your request. It can look at your trip with you and, if your request needs a follow-up, send one to our team.

## Prefer to write?

You can also send us a message about your request from the [Contact page](/contact#message). Our team replies within 24 hours. Include your booking reference so we can find your trip quickly.

## Didn't get the email?

Check your trip page first, since the answer posts there too. Then look in your spam or junk folder. [I didn't get an email from Defera](/help/emails/missing-email) has more to try.`,
  },
  {
    slug: 'feedback-and-complaints',
    title: 'Feedback and complaints',
    summary: 'We want to hear it, good or bad. Tell the Defera Concierge in chat or by phone, or send us a message from the Contact page. If it needs a person, it goes to our team.',
    keywords: ['unhappy', 'bad experience', 'suggestion', 'compliment', 'problem with my booking', 'wrong charge', 'tell Defera'],
    related: ['contact/how-to-contact', 'contact/how-requests-work', 'contact/talk-to-a-person', 'payments/unrecognized-charge'],
    updated: '2026-10-23',
    body: `We want to hear it, whether something went well or didn't. The easiest way to tell us is the Defera Concierge, our AI concierge, in chat or by phone, any time. You can also send us a message from the [Contact page](/contact#message).

If what you're telling us needs a person, the concierge sends it to our team as a request. Someone on our team looks at it, and the answer comes to you by email, usually within 24 hours.

## How to tell us

Here's what to do:

1. **Start a chat or call.** [Chat with the Defera Concierge](/support), or call [{{SUPPORT_PHONE}}](tel:{{SUPPORT_PHONE_TEL}}) to talk to it by voice. Both are open 24/7.
2. **Tell us what happened.** Say which trip it's about, what happened and what you'd like us to do.
3. **Let the concierge send it on.** If it needs a person, the concierge explains what it will send to our team and asks before sending it.
4. **Watch for the answer.** Your request shows as a card on your trip page. The answer posts there and comes to you by email.

## What to include

A few details help our team understand what happened:

- **Your booking reference.** It's the code at the top of your booking confirmation email.
- **What happened, and when.** Your own words are fine.
- **What you'd like us to do.** For example, check a charge, or take another look at your situation.

## If a charge or your trip total looks wrong

Your Trip Statement shows the pricing for your trip: open the trip in **Trips** and tap the download icon in the trip's header row. If something still looks wrong, tell the concierge, and it can send it to our team to check. [A charge I don't recognize](/help/payments/unrecognized-charge) has steps you can take first.

## If the concierge got something wrong

The Defera Concierge is an AI, and it can make mistakes. Your trip page and payment schedule are the source of truth, so if an answer doesn't match what your trip shows, go by your trip page. Tell the concierge what doesn't match and ask it to take another look.

## If an answer didn't settle it

If you've had an answer and you're still stuck or unhappy, say so. The concierge can send your situation to our team so a person can look at it.

## Prefer to write?

Send us a message from the [Contact page](/contact#message) with your booking reference and what happened. Our team replies within 24 hours.

## Our commitments

Our [Charter](/charter) sets out Defera's commitments to you.`,
  },
  {
    slug: 'urgent-help-before-a-flight',
    title: 'Getting urgent help before your flight',
    summary: 'Flying soon and something’s wrong? Tell the Defera Concierge it’s urgent. An urgent request is the fastest way to reach a person on our team.',
    keywords: ['emergency', 'flight today', 'flight tomorrow', 'stranded', 'at the airport', 'cancelled flight', 'last minute', 'travel problem'],
    related: ['changes-cancellations/airline-schedule-change', 'confirmation-and-travel/paid-off-no-confirmation', 'confirmation-and-travel/airline-says-contact-third-party', 'contact/talk-to-a-person'],
    updated: '2026-10-05',
    body: `If something's wrong and your flight is coming up soon, [chat with the Defera Concierge](/support), our AI concierge, and say it's urgent. It can send an urgent request to our team, which is the fastest way to reach a person.

We know how stressful a travel problem is when the clock is ticking. A clear first message helps: include your booking reference, when you fly and what's happening, so our team has what it needs from the start.

## What counts as urgent

Urgent means a time-critical travel problem, such as:

- something wrong with a flight that's coming up soon;
- a problem with your trip while you're away.

If it can wait a day, a standard request is fine. Our team usually answers those within 24 hours.

## How to get an urgent request in fast

Here's what to do:

1. **Open the chat.** Go to the support chat, or tap the message button in your trip's header row.
2. **Lead with the urgency.** Start by saying it's urgent and when your flight leaves.
3. **Describe what's happening.** Say what's wrong and what you need from us.
4. **Say yes to the request.** When the concierge offers to send a request to our team, say yes.

Your request shows as a card on your trip page. The answer posts there and comes to you by email.

## What to include

- **Your booking reference.** It's the code at the top of your booking confirmation email.
- **Your flight.** The date and time, and which travelers are affected.
- **What's happening.** In your own words, including anything the airline has told you.
- **What you need from us.** For example, help with a flight the airline has changed.

## Should I call instead?

You can call [{{SUPPORT_PHONE}}](tel:{{SUPPORT_PHONE_TEL}}) 24/7 if you'd rather talk, but it's the same AI concierge as the chat, not a faster lane. Either way, the urgent request is what gets your situation to a person, and you can ask for one by phone too.

## If the airline cancels, delays or changes your flight

You haven't done anything wrong. Cancellations and delays are handled by the airline that operates your flight, under the rules that apply, such as US Department of Transportation rules, or EU261 in Europe. We help you navigate it: tell the concierge what's happening.

If the airline tells you to contact the company you booked with, that's us. [If the airline changes or cancels your flight](/help/changes-cancellations/airline-schedule-change) has more.

## If you can't find your booking in the airline's app

If you have your confirmation number but your booking isn't showing in an airline's app, check whether the flight is operated by a partner airline. Your itinerary shows this as "Operated by" another airline. Your booking usually lives with the airline that operates the flight.

## If you've paid in full but don't see your confirmation number

Your confirmation number is released once your trip is paid in full. After your final payment, a "Confirmation Number Release" card appears on your trip. If you don't see one, tap **Finished paying for your trip?** in your trip's payment schedule. If your flight is very soon, tell the concierge it's urgent. [I've paid off my trip. Where's my confirmation number?](/help/confirmation-and-travel/paid-off-no-confirmation) has more.

## What we can't promise

We can't promise a particular outcome. But an urgent request is the fastest way to get your situation in front of a person on our team.`,
  },
];
